import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import logo from '../../assets/justservicelogo.png';
import './Navbar.css';

const typeTab = {
  new_user: 'Public', new_business: 'Public', admin_broadcast: 'Public',
  new_message: 'Enquiry', new_enquiry: 'Enquiry',
  business_approved: 'Private', business_rejected: 'Private', new_review: 'Private',
};

export default function Navbar() {
  const { isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifTab, setNotifTab] = useState('Public');
  const [accountOpen, setAccountOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const notifRef = useRef();
  const accountRef = useRef();

  const fetchNotifs = useCallback(() => {
    if (!isLoggedIn) return;
    api.get('/api/notifications/my').then(res => setNotifications(res.data.data || [])).catch(() => {});
    api.get('/api/notifications/unread-count').then(res => setUnreadCount(res.data.data?.count || 0)).catch(() => {});
    // Chat unread — conversations with unread messages
    api.get('/api/chat').then(res => {
      const convs = res.data.data || [];
      const unread = convs.filter(c => {
        const lastMsg = c.messages?.[c.messages.length - 1];
        return lastMsg && lastMsg.senderId?.toString() !== user?.id;
      }).length;
      setChatUnread(unread);
    }).catch(() => {});
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000); // poll every 30s
    return () => clearInterval(t);
  }, [fetchNotifs]);

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNotifOpen = () => {
    setNotifOpen(p => !p);
    if (!notifOpen && unreadCount > 0) {
      api.patch('/api/notifications/read-all').then(() => setUnreadCount(0)).catch(() => {});
    }
  };

  const tabNotifs = {
    Public: notifications.filter(n => ['new_user','new_business','admin_broadcast'].includes(n.type)),
    Private: notifications.filter(n => ['business_approved','business_rejected','new_review'].includes(n.type)),
    Enquiry: notifications.filter(n => ['new_message','new_enquiry'].includes(n.type)),
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/business-listing', label: 'Business Listing', badge: 'FREE' },
    { path: '/blog', label: 'Blog' },
    { path: '/adset', label: 'Advertisement' },
  ];

  return (
    <nav className="navbar">
      <div className="navbar-inner container">
        <Link to="/" className="navbar-logo">
          <img src={logo} alt="JustService24" className="logo-img" />
          <span>Just<span>Service24</span></span>
        </Link>

        <ul className={`navbar-links ${menuOpen ? 'open' : ''}`}>
          {navLinks.map(link => (
            <li key={link.path}>
              <Link to={link.path} className={location.pathname === link.path ? 'active' : ''}
                onClick={() => setMenuOpen(false)}>
                {link.label}
                {link.badge && <span className="free-badge">{link.badge}</span>}
              </Link>
            </li>
          ))}
        </ul>

        <div className="navbar-actions">
          {/* Notifications */}
          <div className="notif-wrapper" ref={notifRef}>
            <button className="nav-icon-btn" onClick={handleNotifOpen}>
              🔔
              {unreadCount > 0 && <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>
            {notifOpen && (
              <div className="notif-dropdown">
                <div className="notif-dropdown-header">
                  <h3>Notifications</h3>
                </div>
                <div className="notif-tabs">
                  {['Public', 'Private', 'Enquiry'].map(tab => (
                    <button key={tab} className={`notif-tab ${notifTab === tab ? 'active' : ''}`}
                      onClick={() => setNotifTab(tab)}>
                      {tab}
                      {tab === 'Enquiry' && chatUnread > 0 && (
                        <span style={{ background: '#ef4444', color: '#fff', borderRadius: '50%', fontSize: 10, padding: '1px 5px', marginLeft: 4 }}>{chatUnread}</span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="notif-list">
                  {tabNotifs[notifTab].length === 0 ? (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                      No notifications yet
                    </div>
                  ) : (
                    tabNotifs[notifTab].map((n, i) => (
                      <div key={i} className="notif-item" style={{ opacity: n.isRead ? 0.6 : 1 }}
                        onClick={() => { if (n.link) { navigate(n.link); setNotifOpen(false); } }}>
                        <span className="notif-item-icon">{n.icon || '🔔'}</span>
                        <div>
                          <p style={{ fontWeight: n.isRead ? 400 : 700, marginBottom: 2 }}>{n.title}</p>
                          <p style={{ fontSize: 12, color: '#94a3b8' }}>{n.message}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Chat */}
          <Link to="/messages" className="nav-icon-btn" style={{ position: 'relative' }}>
            💬
            {chatUnread > 0 && <span className="notif-badge">{chatUnread}</span>}
          </Link>

          {isLoggedIn ? (
            <div className="account-dropdown-wrapper" ref={accountRef}>
              <button className="btn-account" onClick={() => setAccountOpen(p => !p)}>
                <span className="account-avatar">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                Hi, {user?.name?.split(' ')[0] || 'User'}
                <span className="account-chevron">{accountOpen ? '▲' : '▼'}</span>
              </button>
              {accountOpen && (
                <div className="account-dropdown">
                  <div className="account-dropdown-item" onClick={() => { setAccountOpen(false); navigate('/account'); }}>
                    <span>👤</span> My Account
                  </div>
                  <div className="account-dropdown-item logout" onClick={() => {
                    if (!window.confirm('Are you sure you want to logout?')) return;
                    setAccountOpen(false);
                    logout();
                    toast.success('Logged out successfully!');
                    navigate('/login');
                  }}>
                    <span>🚪</span> Logout
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button className="btn-login" onClick={() => navigate('/login')}>Sign Up / Login</button>
          )}

          <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>
    </nav>
  );
}
